/* PasswordResetSectionComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const Renderer = require("./PasswordResetSectionRenderer");

class PasswordResetSectionComponent extends EventEmitter {
    constructor(logger, userService, userSession, router, userFeedback, TenantEntity) {
        super();
        this._renderer = new Renderer(this);

        this.busy = false;
        this._busyQueue = Promise.resolve();
        this.confirmPasswordDirty = false;

        this.onPasswordChange = _.debounce(this._validatePasswords, 333);

        this._logger = logger;
        this._userService = userService;
        this._userSession = userSession;
        this._router = router;
        this._userFeedback = userFeedback;
        this._tenantEntity = TenantEntity;

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer
            .shutdown()
            .then(() => (this._renderer = undefined))
            .catch(() => {
                //TODO - Handle any errors in shutting down.
            });
    }

    async setSelectedTenantId() {
        try {
            this._tenant = await this._tenantEntity.loadTenant(
                this._userSession.getActiveOrganization().id,
                {}
            );
            this._passwordPolicy = this._tenant.getPasswordPolicy() || {
                minPasswordLength: 12,
                minLowerCaseChar: 0,
                minNumericChar: 0,
                minSpecialChar: 0,
                minUpperCaseChar: 0,
            };

            if (!this._tenant.getPasswordPolicy()) {
                this._tenant.setPasswordPolicy(this._passwordPolicy);
                await this._tenant.update();
                console.warn("No password policy defined, setting to defaults");
            }
        } catch (error) {
            console.error("Error in changeSelectedTenantId:", error);
        }
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */

    /*******************************************/

    _validatePasswords(pwd1, pwd2) {
        this.onPasswordChange.cancel();
        if (this.confirmPasswordDirty) {
            if (
                typeof pwd1 === "undefined" ||
                typeof pwd2 === "undefined" ||
                pwd1.length === 0 ||
                pwd2.length === 0
            ) {
                this.emit("passwordValid", false);
            } else if (pwd1 !== pwd2) {
                let errMsg = "Passwords do not match.";
                this.emit("passwordValid", false, errMsg);
            } else {
                //pw policy tests
                const invalidatedResp = this._validatePWPolicy(pwd1);
                if (invalidatedResp.length > 0) {
                    this.emit("passwordValid", false, invalidatedResp);
                } else {
                    this.emit("passwordValid", true);
                }
            }
        }
    }

    _validatePWPolicy(inputPwd) {
        const policy = this._passwordPolicy;
        const errors = [];

        // Check length, min 12 if not set
        const minPasswordLength = policy.minPasswordLength ?? 12;
        if (inputPwd.length < minPasswordLength) {
            errors.push(`at least ${minPasswordLength} characters`);
        }

        // Define character type validators
        const validators = [
            {
                key: "minUpperCaseChar",
                name: "uppercase letter",
                regex: /[A-Z]/g,
            },
            {
                key: "minLowerCaseChar",
                name: "lowercase letter",
                regex: /[a-z]/g,
            },
            {
                key: "minNumericChar",
                name: "digit",
                regex: /[0-9]/g,
            },
            {
                key: "minSpecialChar",
                name: "special character",
                regex: /[-\\'`~!#*$@_%+=.,^&(){}[\]|;:"<>?\\]/g,
            },
        ];

        // Check each character type requirement
        const missingTypes = [];
        validators.forEach(({key, name, regex}) => {
            const required = policy[key];
            if (required > 0) {
                const count = (inputPwd.match(regex) || []).length;
                if (count < required) {
                    missingTypes.push({
                        count: required,
                        name,
                    });
                }
            }
        });

        // Format character type errors
        if (missingTypes.length > 0) {
            const parts = missingTypes.map(({count, name}) => {
                const countStr = this._numberToWord(count) || count;
                const plural = count > 1 ? "s" : "";
                return `${countStr} ${name}${plural}`;
            });
            errors.push(`at least ${parts.join(", ")}`);
        }

        // Build response
        if (errors.length === 0) {
            return "";
        }

        if (errors.length === 1) {
            return `Password must contain ${errors[0]}.`;
        }

        const last = errors.pop();
        return `Password must contain ${errors.join(", ")} and ${last}.`;
    }

    _numberToWord(num) {
        const words = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
        return words[num] || null;
    }

    /** Disable inputs when busy */
    _busy(isBusy) {
        this._busyQueue = this._busyQueue.then(() => {
            if (typeof isBusy === "undefined") {
                isBusy = true;
            }

            this.busy = isBusy;
            this.emit("busy", this.busy);

            return this.busy
                ? this._userFeedback.displayLoading()
                : this._userFeedback.hideDisplayed();
        });
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */

    /*******************************************/

    resetUserPassword(password) {
        this._busy();

        return this._userService
            .resetPassword({password: password})
            .then(() => {
                this.emit("passwordChangeSuccess");
                this._busy(false);
                let loginTimer = setInterval(() => {
                    clearInterval(loginTimer);
                    this._logout();
                }, 3000);
            })
            .catch((err) => {
                this._logger.error("Error when trying to reset password.", err);
                if (err.error) {
                    console.error(err);
                    this._userFeedback.displayError({
                        errorMsg: err.error.errorMsg,
                        errorCode: err.error.errorCode,
                        error: err.error.errorCode,
                    });
                    this.emit("passwordError", err.error.errorMsg);
                } else {
                    this._userFeedback.displayError({
                        name: "Unable to connect.",
                        title: "Unable to connect.",
                        message: "This can occur if required services are not running.",
                    });
                }

                return Promise.resolve();
            });
    }

    _logout() {
        const token = this._userSession.token || localStorage["Steve.session.token"];
        localStorage.clear();
        location.href = `${window.origin}/session/logout/${token}%2Freset`;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

PasswordResetSectionComponent.$inject = [
    "$teve.logger",
    "$teve.services.User",
    "$teve.userSession",
    "$teve.router",
    "$teve.ui.userFeedback",
    "$teve.entities.Tenant",
];

module.exports = PasswordResetSectionComponent;
